/**
 * Layout maths for the Carpool Schedule week grid.
 *
 * The grid is hours down, days across. Rather than showing a full 24 hours —
 * mostly empty — it crops to the hours the school actually uses, with an hour of
 * padding either side so a block never touches the edge.
 */

export const DEFAULT_START_HOUR = 6;
export const DEFAULT_END_HOUR = 19;
export const PADDING_HOURS = 1;

export function startOfWeek(dateKey) {
  const date = new Date(`${dateKey}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  // Monday-first: getUTCDay() is 0 for Sunday.
  const offset = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - offset);
  return date.toISOString().slice(0, 10);
}

export function addDays(dateKey, days) {
  const date = new Date(`${dateKey}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function formatMinutes(totalMinutes) {
  const normalized = ((Math.round(totalMinutes) % 1440) + 1440) % 1440;
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  const suffix = hours < 12 ? 'AM' : 'PM';
  const display = hours % 12 === 0 ? 12 : hours % 12;
  return `${display}:${String(minutes).padStart(2, '0')} ${suffix}`;
}

export function formatDayLabel(dateKey) {
  const date = new Date(`${dateKey}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'numeric', day: 'numeric' });
}

/**
 * A block is drawn for the line's live window when it has one, and for a short
 * marker at the dismissal time when it does not — an always-live line has no
 * meaningful start and end to draw.
 */
export function getBlockRange(line) {
  if (line?.dismissal_time === null || line?.dismissal_minutes === null) return null;

  if (Number.isFinite(line.window_start_minutes) && Number.isFinite(line.window_end_minutes)) {
    return { start: line.window_start_minutes, end: line.window_end_minutes, bounded: true };
  }

  // No duration set: show a nominal 30-minute marker so it is still visible.
  return { start: line.dismissal_minutes, end: line.dismissal_minutes + 30, bounded: false };
}

/**
 * Crop the grid to the hours actually in use, padded either side. Falls back to
 * a sensible school day when nothing is scheduled at all.
 */
export function getVisibleHourRange(days = []) {
  let earliest = null;
  let latest = null;

  days.forEach((day) => {
    (day.lines || []).forEach((line) => {
      const range = getBlockRange(line);
      if (!range) return;
      earliest = earliest === null ? range.start : Math.min(earliest, range.start);
      latest = latest === null ? range.end : Math.max(latest, range.end);
    });
  });

  if (earliest === null || latest === null) {
    return { startHour: DEFAULT_START_HOUR, endHour: DEFAULT_END_HOUR };
  }

  const startHour = Math.max(0, Math.floor(earliest / 60) - PADDING_HOURS);
  const endHour = Math.min(24, Math.ceil(latest / 60) + PADDING_HOURS);

  // Always show at least a few hours so a single short block is not a sliver.
  return endHour - startHour < 4
    ? { startHour: Math.max(0, startHour - 1), endHour: Math.min(24, startHour + 4) }
    : { startHour, endHour };
}

/** Position a block as percentages within the cropped grid. */
export function getBlockPosition(line, startHour, endHour) {
  const range = getBlockRange(line);
  if (!range) return null;

  const gridStart = startHour * 60;
  const gridSpan = (endHour - startHour) * 60;
  if (gridSpan <= 0) return null;

  const top = ((range.start - gridStart) / gridSpan) * 100;
  const height = ((range.end - range.start) / gridSpan) * 100;

  return {
    top: Math.max(0, Math.min(100, top)),
    height: Math.max(1.5, Math.min(100 - Math.max(0, top), height)),
    bounded: range.bounded,
    label: `${formatMinutes(range.start)} – ${formatMinutes(range.end)}`,
  };
}

/**
 * Share the width of a day column between blocks that overlap in time.
 *
 * Every block used to be drawn at the full column width, so two lines at the
 * same time landed exactly on top of each other and their labels rendered
 * through one another — unreadable, and impossible to tell from a single line.
 *
 * Blocks are grouped into clusters of mutually-overlapping runs, and within a
 * cluster each takes the first column free at its start time, the way a
 * calendar lays out concurrent events. Blocks that merely touch end-to-start
 * (12:00–13:00 and 13:00–14:00) do not overlap, so they share a column and keep
 * the full width.
 *
 * @returns {Map<number, {columnIndex: number, columnCount: number}>} keyed by carpool_line_id
 */
export function getDayBlockLayout(lines = []) {
  const blocks = (lines || [])
    .map((line) => ({ line, range: getBlockRange(line) }))
    .filter((block) => block.range)
    .sort((left, right) => (
      left.range.start - right.range.start
      || left.range.end - right.range.end
      || Number(left.line.carpool_line_id) - Number(right.line.carpool_line_id)
    ));

  const layout = new Map();
  let cluster = [];
  let clusterEnd = null;

  // A cluster is laid out only once nothing further can join it, because its
  // column count — and so every member's width — depends on the whole group.
  const placeCluster = () => {
    if (!cluster.length) return;

    const columnEnds = [];
    cluster.forEach((block) => {
      let columnIndex = columnEnds.findIndex((end) => end <= block.range.start);
      if (columnIndex === -1) {
        columnEnds.push(block.range.end);
        columnIndex = columnEnds.length - 1;
      } else {
        columnEnds[columnIndex] = block.range.end;
      }
      block.columnIndex = columnIndex;
    });

    cluster.forEach((block) => layout.set(block.line.carpool_line_id, {
      columnIndex: block.columnIndex,
      columnCount: columnEnds.length,
    }));

    cluster = [];
    clusterEnd = null;
  };

  blocks.forEach((block) => {
    if (clusterEnd !== null && block.range.start >= clusterEnd) placeCluster();
    cluster.push(block);
    clusterEnd = clusterEnd === null ? block.range.end : Math.max(clusterEnd, block.range.end);
  });
  placeCluster();

  return layout;
}

/**
 * Lines whose live windows overlap on the same day. Two lines live at once over
 * the same ground is what turns one arrival into duplicate pickup events.
 */
export function findOverlapsForDay(day) {
  const lines = (day?.lines || []).filter((line) => line.geofenced && getBlockRange(line));
  const clashes = [];

  for (let i = 0; i < lines.length; i += 1) {
    for (let j = i + 1; j < lines.length; j += 1) {
      const a = getBlockRange(lines[i]);
      const b = getBlockRange(lines[j]);
      // Unbounded lines are live all day, so they always clash.
      const clash = (!a.bounded || !b.bounded) || (a.start <= b.end && b.start <= a.end);
      if (clash) clashes.push([lines[i], lines[j]]);
    }
  }

  return clashes;
}

/**
 * Where "now" sits in the cropped grid, as a percentage — or null when the
 * current time is outside the hours on screen, or the day is not shown at all.
 *
 * `nowMinutes` must be the *school's* local time, not the viewer's: an
 * administrator in another timezone would otherwise see the marker in the wrong
 * place on a schedule that is enforced in the school's own time.
 */
export function getCurrentTimePosition({ days = [], todayKey, nowMinutes, startHour, endHour } = {}) {
  if (!Number.isFinite(nowMinutes) || !todayKey) return null;
  if (!days.some((day) => day.date === todayKey)) return null;

  const gridStart = startHour * 60;
  const gridSpan = (endHour - startHour) * 60;
  if (gridSpan <= 0) return null;
  if (nowMinutes < gridStart || nowMinutes > gridStart + gridSpan) return null;

  return {
    top: ((nowMinutes - gridStart) / gridSpan) * 100,
    columnIndex: days.findIndex((day) => day.date === todayKey),
    label: formatMinutes(nowMinutes),
  };
}
